import {
  DonationTrainOverlay,
  DonationTrainOverlayBackgroundColors,
  toDonationTrainOverlayBackgroundColor,
} from "@playlive/overlay-core/overlays/donation-train";
import { deepSignal } from "deepsignal";
import { useEffect, useState } from "preact/hooks";
import YesNoInput from "@/components/input/yes-no.input.jsx";
import CustomizeSection from "@/components/overlays/shared/customize-section.jsx";
import LinkSetup from "@/components/overlays/social/link-setup/link-setup.jsx";
import HorizontalCustomizeWrapper from "@/components/overlays/wrapper/horizonal-customize-wrapper.jsx";
import ScaledOverlayWrapper from "@/components/overlays/wrapper/scaled-overlay-wrapper.jsx";
import ColorPicker from "@/components/pickers/color-picker/color.picker.jsx";
import ColorPickerLabel from "@/components/pickers/color-picker/color-picker-label.jsx";
import CharitySection from "@/components/shared/charity-section.jsx";
import useCommon from "@/hooks/useCommon.js";
import { isYes } from "@/util/common.js";
import { CHARITY } from "@/util/const.js";
import { getTrain, getTrainDonations, getTrainEnd } from "@/util/donation-trains.js";
import { useRulesets } from "./donation-train-overlay.hooks.js";
import { DonationTrainOverlayRulesets } from "./donation-train-overlay-rulesets.jsx";

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  donationTrainTheme: "regular",
  donationTrainPosition: "top",
  donationTrainMaxVisible: 1,
  donationTrainColor: "default-red",
  donationTrainShowActivationMessage: "yes",
});

export default function DonationTrainOverlayPage() {
  const useBetaOverlays = isYes(import.meta.env.VITE_BETA_OVERLAYS || "no");
  const { overlayInfo, overlayURL, setConfigState, tiltifyConfig } = useCommon({
    config,
    forceTiltify: true,
    urlBase: useBetaOverlays ? "overlays/donation-train" : "donation-train",
  });

  const [trains, setTrains] = useState([getTrain("1", "1", 1)]);

  useEffect(() => {
    const interval = setInterval(() => {
      const newTrains = trains.map((train) => {
        train.donationCount += 1;
        train.lastDonation = new Date();
        train.trainEnd = getTrainEnd(5);
        train.donations.push(getTrainDonations("1", 1)[0]);
        return train;
      });

      setTrains(newTrains);
    }, 30000);

    return () => clearInterval(interval);
  });

  const { rulesets, createRuleset, updateRuleset, deleteRuleset } = useRulesets(
    tiltifyConfig.tiltifyCampaignID,
  );

  function filterColors(color) {
    return DonationTrainOverlayBackgroundColors.find((c) => c === color);
  }

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col xl:flex-row overflow-x-hidden">
        <div className="example_overlay relative w-full min-h-40">
          <ScaledOverlayWrapper
            allowScaling={false}
            defaultScale={"scale-100"}
            innerClassName="h-[554px]"
          >
            <DonationTrainOverlay
              trains={trains}
              position={config.donationTrainPosition}
              theme={config.donationTrainTheme}
              maxTrainsVisible={config.donationTrainMaxVisible}
              demoMode
              onFactChange={() => console.log("fact change")}
              onTrainEnding={() => console.log("train ending")}
              onTrainEnded={() => console.log("train ended")}
              backgroundColor={toDonationTrainOverlayBackgroundColor(config.donationTrainColor)}
              status={"ACTIVE"}
            />
          </ScaledOverlayWrapper>
        </div>

        <HorizontalCustomizeWrapper className="flex flex-col mb-0!">
          <CustomizeSection title="Appearance">
            <ColorPickerLabel color={config.donationTrainColor} labelText="Train color" />

            <ColorPicker
              fieldName="donationTrainColor"
              currentColor={config.donationTrainColor}
              filterFunction={filterColors}
              onChange={setConfigState}
            />

            <YesNoInput
              currentValue={config.donationTrainShowActivationMessage}
              name="donationTrainShowActivationMessage"
              onChange={setConfigState}
              mode="checkbox"
              label="Show Activation Message"
            >
              <span className="text-xs text-gray-500">
                {" "}
                Show 'Donate now to activate the donation train!' when there are no trains
              </span>
            </YesNoInput>
          </CustomizeSection>

          <CustomizeSection title="Time and Amounts">
            <DonationTrainOverlayRulesets
              updateRuleset={updateRuleset}
              createRuleset={createRuleset}
              deleteRuleset={deleteRuleset}
              rulesets={rulesets}
            />
          </CustomizeSection>
        </HorizontalCustomizeWrapper>
      </div>

      <CharitySection onChange={setConfigState} charityType={config.charityType} />

      <LinkSetup
        showCopyDemoModeLinkButton
        charityType={config.charityType}
        obsDimensions={overlayInfo.obsDimensions}
        overlayName={overlayInfo.name}
        overlayURL={overlayURL}
        childrenSlot="stepTwo"
        childrenSlotPosition="after"
      />
    </div>
  );
}
